import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { COMPANY } from "../data/company";
import SEO from "../components/SEO";
import Lightbox from "../components/Lightbox";
import { imageUrl } from "../lib/images";

export default function Gallery() {
  const [galleries, setGalleries] = useState([]);
  const [activeFilter, setActiveFilter] = useState("alle");
  const [loading, setLoading] = useState(true);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [activeImageIndex, setActiveImageIndex] = useState(0);

  // Fetch from CMS collection 'galleries'
  useEffect(() => {
    let ignore = false;
    async function loadData() {
      try {
        setLoading(true);
        const res = await fetch("/_api/public/content/galleries");
        if (res.ok) {
          const data = await res.json();
          if (Array.isArray(data) && !ignore) {
            setGalleries(data);
          }
        }
      } catch (err) {
        // silent fallback
      } finally {
        if (!ignore) setLoading(false);
      }
    }
    loadData();
    return () => {
      ignore = true;
    };
  }, []);

  // Fallback items if CMS is slow or offline
  const fallbackGalleries = [
    {
      title: "Døre og vinduer",
      service_slug: "doere-og-vinduer",
      caption: "Udskiftning og præcis montering af vinduer og terrassedøre – vores store passion",
      alt_text: "Montering af energieffektive døre og vinduer i høj kvalitet",
      images: [
        "/_assets/doere-vinduer-parcelhus.jpg",
        "/_assets/doere-vinduer-terrasse.jpg",
      ],
    },
    {
      title: "Træterrasser & udendørsarealer",
      service_slug: "traeterrasser-og-udendoersarealer",
      caption: "Eksklusive træterrasser og udemiljøer skabt til udeliv",
      alt_text: "Opførelse af træterrasser og udendørsarealer",
      images: [
        "/_assets/ny-traeterrasse-dansk-parcelhus-hero.jpg",
        "/_assets/traeterrasse-udendoers-havemiljoe.jpg",
      ],
    },
    {
      title: "Indendørs renovering – lofter & vægge",
      service_slug: "indendoers-renovering",
      caption: "Moderne lofter, akustikpaneler og renovering af indvendige vægge",
      alt_text: "Indendørs tømrerrenovering med akustikloft og vægge",
      images: [
        "/_assets/indendoers-renovering-lofter-vaegge.jpg",
        "/_assets/indendoers-toemrer-akustikloft-vaegge.jpg",
      ],
    },
  ];

  const effectiveGalleries = galleries.length > 0 ? galleries : fallbackGalleries;

  // Flatten images into displayable gallery cards with their category
  const allCards = [];
  effectiveGalleries.forEach((gal) => {
    let sSlug = gal.service_slug || gal.slug?.da || gal.slug || "andet";
    // Normalize older slugs if present
    if (sSlug === "bindingsvaerk") sSlug = "traeterrasser-og-udendoersarealer";
    if (sSlug === "tagarbejde") sSlug = "indendoers-renovering";

    const imgs = gal.images && gal.images.length > 0 ? gal.images : gal.cover_image || [];
    imgs.forEach((img, idx) => {
      allCards.push({
        id: `${sSlug}-${idx}`,
        serviceSlug: sSlug,
        categoryTitle: gal.title || (sSlug === "doere-og-vinduer" ? "Døre og vinduer" : sSlug === "traeterrasser-og-udendoersarealer" ? "Træterrasser & uderum" : "Indendørs renovering"),
        src: img,
        caption: gal.caption || gal.title || "Tømrerarbejde",
        alt: gal.alt_text || `${gal.title} - billede ${idx + 1}`,
      });
    });
  });

  // Filter cards
  const filteredCards =
    activeFilter === "alle"
      ? allCards
      : allCards.filter((card) => card.serviceSlug === activeFilter);

  const imagesForLightbox = filteredCards.map((c) => c.src);
  const captionsForLightbox = filteredCards.map((c) => `${c.categoryTitle}: ${c.caption}`);

  const openLightbox = (index) => {
    setActiveImageIndex(index);
    setLightboxOpen(true);
  };

  const filterTabs = [
    { label: "Alle opgaver", value: "alle" },
    { label: "Døre og vinduer", value: "doere-og-vinduer" },
    { label: "Træterrasser & uderum", value: "traeterrasser-og-udendoersarealer" },
    { label: "Indendørs renovering", value: "indendoers-renovering" },
  ];

  return (
    <>
      <SEO
        title="Galleri"
        description={`Se projektgalleriet for ${COMPANY.name} i ${COMPANY.city}. Se eksempler på døre & vinduer, træterrasser og indendørs renovering af lofter og vægge på Fyn.`}
        path="/galleri"
        image="/_assets/ny-traeterrasse-dansk-parcelhus-hero.jpg"
      />

      {/* Page Header */}
      <section className="bg-[#111111] text-white py-16 sm:py-20 border-b border-neutral-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <div className="flex items-center gap-2 text-[#A78E51] font-bold text-xs uppercase tracking-widest mb-3">
              <Link to="/" className="hover:underline">Forside</Link>
              <i className="fa-solid fa-angle-right text-[10px] text-neutral-500" aria-hidden="true" />
              <span>Galleri</span>
            </div>
            <h1 className="text-3xl sm:text-5xl font-extrabold tracking-tight text-white">
              Projektgalleri
            </h1>
            <p className="mt-4 text-base sm:text-lg text-neutral-300 leading-relaxed">
              Her finder du en samlet oversigt over vores håndværksmæssige arbejde. Filtrér efter ydelse for at se eksempler på døre & vinduer, opførelse af træterrasser eller indendørs renovering af lofter og vægge.
            </p>
          </div>
        </div>
      </section>

      {/* Filter and Grid */}
      <section className="py-12 sm:py-16 bg-[#FAFAF8] min-h-[50vh]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Filter Bar */}
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-10 pb-6 border-b border-neutral-200">
            {/* Filter buttons */}
            <div className="flex flex-wrap items-center gap-2" role="tablist" aria-label="Filtrer galleri">
              {filterTabs.map((tab) => (
                <button
                  key={tab.value}
                  type="button"
                  role="tab"
                  aria-selected={activeFilter === tab.value}
                  onClick={() => setActiveFilter(tab.value)}
                  className={`px-4 py-2 rounded-md text-xs sm:text-sm font-bold transition-all focus:outline-none focus:ring-2 focus:ring-[#A78E51] ${
                    activeFilter === tab.value
                      ? "bg-[#111111] text-white shadow-sm"
                      : "bg-white text-neutral-700 hover:text-[#111111] hover:bg-neutral-100 border border-neutral-300"
                  }`}
                >
                  {tab.label}
                </button>
              ))}
            </div>

            <div className="text-xs text-neutral-500 flex items-center gap-1.5 self-start sm:self-auto">
              <i className="fa-solid fa-images text-[#A78E51]" aria-hidden="true" />
              <span>Viser {filteredCards.length} billeder</span>
              <span className="text-neutral-400 ml-2">(Illustrationsbilleder)</span>
            </div>
          </div>

          {/* Loading state */}
          {loading && allCards.length === 0 ? (
            <div className="py-20 text-center text-neutral-500">
              <i className="fa-solid fa-spinner fa-spin text-2xl text-[#A78E51] mb-3 block" aria-hidden="true" />
              <span>Henter projektbilleder...</span>
            </div>
          ) : filteredCards.length === 0 ? (
            <div className="py-16 text-center text-neutral-500 bg-white rounded-lg border border-neutral-200 p-8">
              <i className="fa-solid fa-image text-3xl text-neutral-300 mb-3 block" aria-hidden="true" />
              <p>Der er ingen billeder i denne kategori i øjeblikket.</p>
              <button
                type="button"
                onClick={() => setActiveFilter("alle")}
                className="mt-4 inline-flex items-center text-sm font-bold text-[#A78E51] hover:underline"
              >
                Vis alle billeder
              </button>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredCards.map((card, idx) => (
                <div
                  key={card.id || idx}
                  onClick={() => openLightbox(idx)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      openLightbox(idx);
                    }
                  }}
                  tabIndex={0}
                  role="button"
                  aria-label={`Åbn ${card.categoryTitle} billede ${idx + 1}`}
                  className="group relative bg-white rounded-lg overflow-hidden border border-neutral-200 shadow-sm cursor-pointer focus:outline-none focus:ring-2 focus:ring-[#A78E51] transition-all flex flex-col justify-between"
                >
                  <div className="aspect-[4/3] overflow-hidden bg-neutral-100 relative">
                    <img
                      src={imageUrl(card.src, "width=800,quality=80,format=auto")}
                      alt={card.alt}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      loading="lazy"
                    />

                    {/* Category pill on image */}
                    <div className="absolute top-3 left-3 bg-[#111111]/85 text-white text-[11px] font-semibold px-2.5 py-1 rounded shadow-sm border border-neutral-700">
                      {card.categoryTitle}
                    </div>

                    {/* Magnifier overlay */}
                    <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 group-focus-visible:opacity-100 transition-opacity flex items-center justify-center p-4">
                      <span className="p-3 rounded-full bg-[#111111]/90 text-[#A78E51] border border-[#A78E51]/50 text-sm shadow-md transform translate-y-2 group-hover:translate-y-0 transition-transform">
                        <i className="fa-solid fa-magnifying-glass-plus" aria-hidden="true" />
                      </span>
                    </div>
                  </div>

                  <div className="p-4 bg-white border-t border-neutral-100 flex items-center justify-between">
                    <div>
                      <h3 className="font-bold text-sm text-[#111111]">
                        {card.categoryTitle}
                      </h3>
                      <p className="text-xs text-neutral-500 line-clamp-1 mt-0.5">
                        {card.caption}
                      </p>
                    </div>
                    <span className="text-xs text-[#A78E51] font-semibold group-hover:translate-x-1 transition-transform">
                      &rarr;
                    </span>
                  </div>
                </div>
              ))}
            </div>
          )}


        </div>
      </section>

      {/* Lightbox Component */}
      <Lightbox
        isOpen={lightboxOpen}
        images={imagesForLightbox}
        captions={captionsForLightbox}
        currentIndex={activeImageIndex}
        onClose={() => setLightboxOpen(false)}
        onNext={() => setActiveImageIndex((prev) => (prev + 1) % imagesForLightbox.length)}
        onPrev={() =>
          setActiveImageIndex((prev) => (prev - 1 + imagesForLightbox.length) % imagesForLightbox.length)
        }
      />
    </>
  );
}
